import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { useMemo, useEffect } from "react";
import { Leaf, Building2, Download } from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import { applyPlugin } from "jspdf-autotable";
applyPlugin(jsPDF);

export const SocietyFinancials = ({ formData, setFormData, setSocietyCosts }) => {

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const isGreenfield = formData.projectMode === "greenfield";

  const calculations = useMemo(() => {
    // If Greenfield, all society expenses are zero
    if (isGreenfield) {
      return {
        shiftingCharges: 0,
        corpusCharges: 0,
        bettermentCharges: 0,
        totalRentalCost: 0,
        monthlyRentFlats: 0,
        monthlyRentOffices: 0,
        monthlyRentShops: 0,
        depositCost: 0,
        brokerageCost: 0,
        pmcCost: 0,
        bgCost: 0,
        gstFlats: 0,
        gstOffices: 0,
        gstShops: 0,
        totalGST: 0,
        totalSocietyExpenses: 0,
        isGreenfield: true,
        jvRatioPercent: parseFloat(formData.jvRatioPercent) || 30
      };
    }

    // Area conversions (sft to sq mtr)
    const conversionFactor = 0.092903;
    const areaFlatsValue = parseFloat(formData.areaFlats) || 0;
    const areaOfficesValue = parseFloat(formData.areaOffices) || 0;
    const areaShopsValue = parseFloat(formData.areaShops) || 0;
    const areaFlatsSqMts = areaFlatsValue * conversionFactor;
    const areaOfficesSqMts = areaOfficesValue * conversionFactor;
    const areaShopsSqMts = areaShopsValue * conversionFactor;
    
    const numMembers = parseInt(formData.numMembers) || 0;
    const duration = parseInt(formData.projectDuration) || 0;

    // 1. Shifting Charges
    const shiftingCharges = formData.includeShiftingCharges === false
      ? 0
      : numMembers * (parseFloat(formData.shiftingCharges) || 0);

    // 2. Corpus Charges
    let corpusCharges = 0;
    if (formData.includeCorpusCharges !== false) {
      if (formData.corpusChargesType === "per_sft") {
        corpusCharges = areaFlatsValue * (parseFloat(formData.corpusCharges) || 0);
        if (formData.hasOffices && areaOfficesValue > 0) corpusCharges += areaOfficesValue * (parseFloat(formData.corpusCharges) || 0);
        if (formData.hasShops && areaShopsValue > 0) corpusCharges += areaShopsValue * (parseFloat(formData.corpusCharges) || 0);
      } else {
        corpusCharges = numMembers * (parseFloat(formData.corpusCharges) || 0);
      }
    }

    // 3. Betterment Charges - separate for flats, offices, shops
    const bettermentFlatsRate = parseFloat(formData.bettermentCharges) || 0;
    const bettermentOfficesRate = parseFloat(formData.bettermentChargesOffices) || bettermentFlatsRate;
    const bettermentShopsRate = parseFloat(formData.bettermentChargesShops) || bettermentFlatsRate;

    let bettermentCharges = 0;
    if (formData.includeBettermentCharges !== false) {
      if (formData.bettermentChargesType === "per_member") {
        // Per flat/unit basis - use numMembers for flats, estimate office/shop units from area ratio
        bettermentCharges = numMembers * bettermentFlatsRate;
        // Add offices betterment if different rate and offices exist
        if (formData.hasOffices && areaOfficesValue > 0 && bettermentOfficesRate !== bettermentFlatsRate) {
          const officeUnits = Math.ceil(areaOfficesValue / (areaFlatsValue / Math.max(numMembers, 1)));
          bettermentCharges += officeUnits * (bettermentOfficesRate - bettermentFlatsRate);
        }
        // Add shops betterment if different rate and shops exist
        if (formData.hasShops && areaShopsValue > 0 && bettermentShopsRate !== bettermentFlatsRate) {
          const shopUnits = Math.ceil(areaShopsValue / (areaFlatsValue / Math.max(numMembers, 1)));
          bettermentCharges += shopUnits * (bettermentShopsRate - bettermentFlatsRate);
        }
      } else {
        // Per sft basis - straightforward per area type
        bettermentCharges = areaFlatsValue * bettermentFlatsRate;
        if (formData.hasOffices && areaOfficesValue > 0) {
          bettermentCharges += areaOfficesValue * bettermentOfficesRate;
        }
        if (formData.hasShops && areaShopsValue > 0) {
          bettermentCharges += areaShopsValue * bettermentShopsRate;
        }
      }
    }

    // 4. Rental Costs (year-over-year increment)
    const isLumpsum = formData.rentType === "per_number" || formData.rentType === "lumpsum";
    const rentIncrementPA = parseFloat(formData.rentIncrementPA) || 5;
    const duration_months = duration;
    
    let monthlyRentFlats = 0;
    let monthlyRentOffices = 0;
    let monthlyRentShops = 0;
    
    if (isLumpsum) {
      // Lumpsum: calculate from unit counts × lumpsum rates
      const count1BHK = parseInt(formData.units1BHK) || 0;
      const count2BHK = parseInt(formData.units2BHK) || 0;
      const count3BHK = parseInt(formData.units3BHK) || 0;
      const rate1BHK = parseFloat(formData.rentLumpsum1BHK) || 0;
      const rate2BHK = parseFloat(formData.rentLumpsum2BHK) || 0;
      const rate3BHK = parseFloat(formData.rentLumpsum3BHK) || 0;
      const rateShops = parseFloat(formData.rentLumpsumShops) || 0;
      const rateOthers = parseFloat(formData.rentLumpsumOthers) || 0;
      const countShops = parseInt(formData.unitsShops) || 0;
      const countOthers = parseInt(formData.unitsOthers) || 0;
      
      monthlyRentFlats = (count1BHK * rate1BHK) + (count2BHK * rate2BHK) + (count3BHK * rate3BHK);
      monthlyRentOffices = countOthers * rateOthers;
      monthlyRentShops = countShops * rateShops;
    } else {
      // Per-sqft mode
      const rentPerSqftFlat = parseFloat(formData.rentPerSqftFlat) || 0;
      const rentPerSqftOffice = parseFloat(formData.rentPerSqftOffice) || 0;
      const rentPerSqftShop = parseFloat(formData.rentPerSqftShop) || 0;
      monthlyRentFlats = areaFlatsValue * rentPerSqftFlat;
      monthlyRentOffices = areaOfficesValue * rentPerSqftOffice;
      monthlyRentShops = areaShopsValue * rentPerSqftShop;
    }

    // If Rent is toggled OFF, zero out everything derived from rent (rental cost, deposit, brokerage)
    if (formData.includeRent === false) {
      monthlyRentFlats = 0;
      monthlyRentOffices = 0;
      monthlyRentShops = 0;
    }
    
    // Separate shop rental duration and increment
    const shopRentDuration = parseInt(formData.shopRentDuration) || duration_months;
    const shopRentIncrement = parseFloat(formData.shopRentIncrement) || rentIncrementPA;
    const baseMonthlyRent = monthlyRentFlats + monthlyRentOffices; // Exclude shops for base calculation
    
    // Calculate total rent for flats and offices with year-over-year increment
    let totalRentalCostFlatsOffices = 0;
    const durationYears = Math.ceil(duration_months / 12);
    for (let year = 0; year < durationYears; year++) {
      const monthsInThisYear = year === durationYears - 1 ? 
        (duration_months % 12 === 0 ? 12 : duration_months % 12) : 12;
      const yearlyMultiplier = Math.pow(1 + rentIncrementPA / 100, year);
      totalRentalCostFlatsOffices += (monthlyRentFlats + monthlyRentOffices) * yearlyMultiplier * monthsInThisYear;
    }
    
    // Calculate shop rental separately with different duration
    let totalRentalCostShops = 0;
    const shopDurationYears = Math.ceil(shopRentDuration / 12);
    for (let year = 0; year < shopDurationYears; year++) {
      const monthsInThisYear = year === shopDurationYears - 1 ? 
        (shopRentDuration % 12 === 0 ? 12 : shopRentDuration % 12) : 12;
      const yearlyMultiplier = Math.pow(1 + shopRentIncrement / 100, year);
      totalRentalCostShops += monthlyRentShops * yearlyMultiplier * monthsInThisYear;
    }
    
    const totalRentalCost = totalRentalCostFlatsOffices + totalRentalCostShops;

    // 5. Refundable Deposit
    let depositCost = 0;
    if (formData.refundableDeposit === "yes") {
      const depositMonths = parseInt(formData.depositMonths) || 0;
      depositCost = baseMonthlyRent * depositMonths;
    }

    // 6. Brokerage
    let brokerageCost = 0;
    if (formData.brokeragePayable === "yes") {
      const brokerageMonths = parseInt(formData.brokerageMonths) || 0;
      brokerageCost = baseMonthlyRent * brokerageMonths;
    }

    // 7. PMC Charges
    let pmcCost = 0;
    if (formData.societyPMCPayable === "yes") {
      pmcCost = parseFloat(formData.pmcAmount) || 0;
    }

    // 8. Bank Guarantee Cost
    // Formula: BG Rate (% p.a.) × Duration (years) × BG Amount
    let bgCost = 0;
    if (formData.bankGuaranteeRequired === "yes") {
      const bgAmount = parseFloat(formData.bankGuaranteeAmount) || 0;
      const bgRate = parseFloat(formData.bgRatePA) || 0;
      const durationYears = duration / 12; // Convert months to years
      bgCost = (bgRate / 100) * durationYears * bgAmount;
    }

    // 9. GST Calculation
    // GST = (Existing Area + Free Area) × 1.1 × RR Rate (raw) × 5%
    const rrFlatRaw = parseFloat(formData.rrRateFlat) || 0;
    const rrShopRaw = parseFloat(formData.rrRateShop) || parseFloat(formData.rrRateFlat) || 0;
    const additionalFreePercent = parseFloat(formData.freeAreaPercentFlats) || parseFloat(formData.additionalFreeAreaPercent) || 0;
    
    // Calculate GST separately for flats and shops
    // Flats GST = (Flat Area + Flat Free Area) × 1.1 × RR Flat × 5%
    const flatFreeArea = areaFlatsSqMts * (additionalFreePercent / 100);
    const gstFlats = formData.hasFlats ? (areaFlatsSqMts + flatFreeArea) * 1.1 * rrFlatRaw * 0.05 : 0;
    
    // Shops GST = (Shop Area + Shop Free Area) × 1.1 × RR Shop × 5%
    const shopFreeArea = areaShopsSqMts * (additionalFreePercent / 100);
    const gstShops = formData.hasShops ? (areaShopsSqMts + shopFreeArea) * 1.1 * rrShopRaw * 0.05 : 0;
    
    // Offices GST (if applicable)
    const officeFreeArea = areaOfficesSqMts * (additionalFreePercent / 100);
    const gstOffices = formData.hasOffices ? (areaOfficesSqMts + officeFreeArea) * 1.1 * rrFlatRaw * 0.05 : 0;
    
    // Total GST
    const totalGST = gstFlats + gstShops + gstOffices;

    // Parking Compensation Payout
    const parkingCompensationPayout = formData.parkingCompensationType === "payout" 
      ? (parseFloat(formData.compensationPayout) || 0) * (parseInt(formData.existingParkings) || 0)
      : 0;

    // Total Society Expenses (deposit excluded - it is refundable, not an expense)
    const totalSocietyExpenses = shiftingCharges + corpusCharges + bettermentCharges + 
                                  totalRentalCost + brokerageCost + 
                                  pmcCost + bgCost + totalGST + parkingCompensationPayout;

    return {
      shiftingCharges,
      corpusCharges,
      bettermentCharges,
      totalRentalCost,
      totalRentalCostFlatsOffices,
      totalRentalCostShops,
      monthlyRentFlats,
      monthlyRentOffices,
      monthlyRentShops,
      parkingCompensationPayout,
      shopRentDuration,
      depositCost,
      brokerageCost,
      pmcCost,
      bgCost,
      gstFlats,
      gstOffices,
      gstShops,
      totalGST,
      // GST formula details
      areaFlatsSqMts,
      areaShopsSqMts,
      areaOfficesSqMts,
      flatFreeArea,
      shopFreeArea,
      officeFreeArea,
      rrFlat: rrFlatRaw,
      rrShop: rrShopRaw,
      totalSocietyExpenses,
      isGreenfield: false
    };
  }, [formData, isGreenfield]);

  // Pass calculations to parent component
  useEffect(() => {
    if (setSocietyCosts && calculations) {
      setSocietyCosts(calculations);
    }
  }, [calculations, setSocietyCosts]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const exportSocietyPDF = () => {
    if (!calculations || calculations.isGreenfield) return;
    const c = calculations;
    const fc = (v) => formatCurrency(v);
    const projectName = formData.projectName || formData.societyName || "Project";

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 20;

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Society Expenses Statement", pageWidth / 2, y, { align: "center" });
    y += 8;
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text(projectName, pageWidth / 2, y, { align: "center" });
    y += 6;
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(`Generated: ${new Date().toLocaleDateString('en-IN')}`, pageWidth / 2, y, { align: "center" });
    doc.setTextColor(0);
    y += 10;

    const rows = [
      ["Shifting Charges", fc(c.shiftingCharges)],
      ["Corpus Charges", fc(c.corpusCharges)],
      ["Betterment Charges", fc(c.bettermentCharges)],
      ["Rental Cost (Flats + Offices)", fc(c.totalRentalCostFlatsOffices)],
      ...(c.totalRentalCostShops > 0 ? [["Rental Cost (Shops)", fc(c.totalRentalCostShops)]] : []),
      ["Total Rental Cost", fc(c.totalRentalCost)],
      ["Brokerage", fc(c.brokerageCost)],
      ["PMC Charges", fc(c.pmcCost)],
      ["Bank Guarantee Cost", fc(c.bgCost)],
    ];

    if (c.totalGST > 0) {
      rows.push(["GST - Flats", fc(c.gstFlats)]);
      if (c.gstOffices > 0) rows.push(["GST - Offices", fc(c.gstOffices)]);
      if (c.gstShops > 0) rows.push(["GST - Shops", fc(c.gstShops)]);
      rows.push(["Total GST", fc(c.totalGST)]);
    }

    if (c.parkingCompensationPayout > 0) {
      rows.push(["Parking Compensation", fc(c.parkingCompensationPayout)]);
    }

    doc.autoTable({
      startY: y,
      head: [["Society Expense Item", "Amount"]],
      body: rows,
      foot: [["TOTAL SOCIETY EXPENSES", fc(c.totalSocietyExpenses)]],
      theme: "grid",
      headStyles: { fillColor: [30, 64, 175], fontSize: 10 },
      footStyles: { fillColor: [22, 163, 74], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 11 },
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: { 1: { halign: "right" } },
    });
    y = doc.lastAutoTable.finalY + 8;

    // Deposit note
    if (c.depositCost > 0) {
      doc.autoTable({
        startY: y,
        head: [["Refundable Items (Not in Expense)", "Amount"]],
        body: [["Refundable Deposit", fc(c.depositCost)]],
        theme: "grid",
        headStyles: { fillColor: [100, 100, 100], fontSize: 10 },
        styles: { fontSize: 9, cellPadding: 3 },
        columnStyles: { 1: { halign: "right" } },
      });
    }

    doc.save(`Society_Expenses_${projectName.replace(/\s+/g, '_')}.pdf`);
    toast.success("Society Expenses exported as PDF");
  };

  // Greenfield mode - show JV Ratio card instead of society expenses
  if (isGreenfield) {
    return (
      <div className="space-y-6">
        <Card className="rounded-sm shadow-sm border-chart-1/50 bg-chart-1/5">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Leaf className="h-5 w-5 text-chart-1" />
              <CardTitle className="font-heading tracking-tight text-chart-1">Greenfield Project</CardTitle>
            </div>
            <CardDescription>No society expenses. Landowner gets a share of total saleable area.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-background rounded-sm border">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="jvRatioPercent">JV Ratio - Landowner Share (%)</Label>
                  <Input
                    id="jvRatioPercent"
                    name="jvRatioPercent"
                    type="number"
                    value={formData.jvRatioPercent}
                    onChange={handleChange}
                    placeholder="30"
                    className="rounded-sm max-w-xs"
                    data-testid="jv-ratio-society-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Percentage of total saleable area given to landowner
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium">Developer Share</p>
                  <p className="font-mono text-2xl text-chart-1">{100 - (parseFloat(formData.jvRatioPercent) || 30)}%</p>
                  <p className="text-xs text-muted-foreground">
                    Remaining area available for sale
                  </p>
                </div>
              </div>
            </div>
            
            <Separator />
            
            <div className="bg-secondary/30 p-4 rounded-sm">
              <h4 className="font-semibold mb-2">Greenfield Benefits</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• No shifting/rental costs to existing tenants</li>
                <li>• No corpus or betterment charges</li>
                <li>• No society PMC or bank guarantee costs</li>
                <li>• Simplified JV structure with landowner</li>
              </ul>
            </div>
            
            <div className="bg-accent/10 p-4 rounded-sm border border-accent/30">
              <div className="flex justify-between items-center">
                <span className="font-semibold text-lg">Total Society Expenses</span>
                <span className="font-mono font-bold text-2xl text-chart-1">₹0</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                All society-related expenses are zero for greenfield projects
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Project Mode Indicator */}
      <Card className="rounded-sm shadow-sm bg-secondary/30">
        <CardContent className="py-4">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-muted-foreground" />
            <span className="font-medium">Redevelopment Project</span>
            <span className="text-sm text-muted-foreground ml-2">
              Values from Quick Input page — change there to update
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Society Inputs Summary - Read Only */}
      <Card className="rounded-sm shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading tracking-tight">Society Terms (from Quick Input)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-3 text-sm">
            <div className="flex justify-between p-2 bg-secondary/50 rounded-sm">
              <span className="text-muted-foreground">Shifting Charges</span>
              <span className="font-semibold">₹{parseInt(formData.shiftingCharges || 0).toLocaleString('en-IN')} / member</span>
            </div>
            <div className="flex justify-between p-2 bg-secondary/50 rounded-sm">
              <span className="text-muted-foreground">Corpus Charges</span>
              <span className="font-semibold">₹{parseInt(formData.corpusCharges || 0).toLocaleString('en-IN')} / {formData.corpusChargesType === "per_sft" ? "sft" : "member"}</span>
            </div>
            <div className="flex justify-between p-2 bg-secondary/50 rounded-sm">
              <span className="text-muted-foreground">Betterment - Flats</span>
              <span className="font-semibold">₹{parseInt(formData.bettermentCharges || 0).toLocaleString('en-IN')} / {formData.bettermentChargesType === "per_sft" ? "sft" : "flat"}</span>
            </div>
            {formData.hasOffices && (
              <div className="flex justify-between p-2 bg-secondary/50 rounded-sm">
                <span className="text-muted-foreground">Betterment - Offices</span>
                <span className="font-semibold">₹{parseInt(formData.bettermentChargesOffices || formData.bettermentCharges || 0).toLocaleString('en-IN')}</span>
              </div>
            )}
            {formData.hasShops && (
              <div className="flex justify-between p-2 bg-secondary/50 rounded-sm">
                <span className="text-muted-foreground">Betterment - Shops</span>
                <span className="font-semibold">₹{parseInt(formData.bettermentChargesShops || formData.bettermentCharges || 0).toLocaleString('en-IN')}</span>
              </div>
            )}
            <div className="flex justify-between p-2 bg-secondary/50 rounded-sm">
              <span className="text-muted-foreground">Parking (4W / 2W per member)</span>
              <span className="font-semibold">{formData.fourWheelerPerMember || 0} / {formData.twoWheelerPerMember || 0}</span>
            </div>
            <div className="flex justify-between p-2 bg-secondary/50 rounded-sm">
              <span className="text-muted-foreground">Rent (Flat)</span>
              <span className="font-semibold">₹{parseInt(formData.rentPerSqftFlat || 0).toLocaleString('en-IN')}/sft/month</span>
            </div>
            {formData.hasOffices && (
              <div className="flex justify-between p-2 bg-secondary/50 rounded-sm">
                <span className="text-muted-foreground">Rent (Office)</span>
                <span className="font-semibold">₹{parseInt(formData.rentPerSqftOffice || 0).toLocaleString('en-IN')}/sft/month</span>
              </div>
            )}
            {formData.hasShops && (
              <div className="flex justify-between p-2 bg-secondary/50 rounded-sm">
                <span className="text-muted-foreground">Rent (Shop)</span>
                <span className="font-semibold">₹{parseInt(formData.rentPerSqftShop || 0).toLocaleString('en-IN')}/sft/month</span>
              </div>
            )}
            <div className="flex justify-between p-2 bg-secondary/50 rounded-sm">
              <span className="text-muted-foreground">Rent Increment PA</span>
              <span className="font-semibold">{formData.rentIncrementPA || 5}%</span>
            </div>
            <div className="flex justify-between p-2 bg-secondary/50 rounded-sm">
              <span className="text-muted-foreground">Project Duration</span>
              <span className="font-semibold">{formData.projectDuration || 0} months</span>
            </div>
            {formData.hasShops && (
              <div className="flex justify-between p-2 bg-secondary/50 rounded-sm">
                <span className="text-muted-foreground">Shop Rent Duration</span>
                <span className="font-semibold">{formData.shopRentDuration || formData.projectDuration || 0} months</span>
              </div>
            )}
            <div className="flex justify-between p-2 bg-secondary/50 rounded-sm">
              <span className="text-muted-foreground">Refundable Deposit</span>
              <span className="font-semibold">{formData.refundableDeposit === "yes" ? `Yes (${formData.depositMonths || 1} month)` : "No"}</span>
            </div>
            <div className="flex justify-between p-2 bg-secondary/50 rounded-sm">
              <span className="text-muted-foreground">Brokerage</span>
              <span className="font-semibold">{formData.brokeragePayable === "yes" ? `Yes (${formData.brokerageMonths || 1} month)` : "No"}</span>
            </div>
            <div className="flex justify-between p-2 bg-secondary/50 rounded-sm">
              <span className="text-muted-foreground">Society PMC</span>
              <span className="font-semibold">{formData.societyPMCPayable === "yes" ? `Yes - ₹${parseInt(formData.pmcAmount || 0).toLocaleString('en-IN')}` : "No"}</span>
            </div>
            {formData.bankGuaranteeRequired === "yes" && (
              <div className="flex justify-between p-2 bg-secondary/50 rounded-sm">
                <span className="text-muted-foreground">BG Rate</span>
                <span className="font-semibold">{formData.bgRatePA || 6}% p.a.</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Society Expenses Summary - Comprehensive Breakdown */}
      {calculations && (
        <Card className="rounded-sm shadow-sm border-chart-1/50" data-testid="society-expenses-summary">
          <CardHeader>
            <CardTitle className="font-heading tracking-tight text-chart-1">Society Expenses Summary</CardTitle>
            <CardDescription>Complete breakdown of all society-related costs</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Member Charges */}
            <div className="space-y-2">
              <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">Member Charges</h4>
              <div className="space-y-2 text-sm bg-secondary/30 p-3 rounded-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Shifting Charges ({formData.numMembers || 0} members × ₹{parseFloat(formData.shiftingCharges || 0).toLocaleString()})</span>
                  <span className="font-mono font-medium">{formatCurrency(calculations.shiftingCharges || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Corpus Charges ({formData.numMembers || 0} members × ₹{parseFloat(formData.corpusCharges || 0).toLocaleString()})</span>
                  <span className="font-mono font-medium">{formatCurrency(calculations.corpusCharges || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Betterment Charges</span>
                  <span className="font-mono font-medium">{formatCurrency(calculations.bettermentCharges || 0)}</span>
                </div>
              </div>
            </div>

            <Separator />

            {/* Rental Costs */}
            <div className="space-y-2">
              <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">Rental & Housing Costs (Paid to Society)</h4>
              <div className="space-y-2 text-sm bg-secondary/30 p-3 rounded-sm">
                {formData.hasFlats && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Rent - Flats ({formData.areaFlats || 0} sft × ₹{formData.rentPerSqftFlat || 0}/sft/mo)</span>
                    <span className="font-mono font-medium">{formatCurrency(calculations.totalRentalCostFlatsOffices ? (calculations.totalRentalCostFlatsOffices * (calculations.monthlyRentFlats / (calculations.monthlyRentFlats + calculations.monthlyRentOffices || 1))) : 0)}</span>
                  </div>
                )}
                {formData.hasOffices && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Rent - Offices ({formData.areaOffices || 0} sft × ₹{formData.rentPerSqftOffice || 0}/sft/mo)</span>
                    <span className="font-mono font-medium">{formatCurrency(calculations.totalRentalCostFlatsOffices ? (calculations.totalRentalCostFlatsOffices * (calculations.monthlyRentOffices / (calculations.monthlyRentFlats + calculations.monthlyRentOffices || 1))) : 0)}</span>
                  </div>
                )}
                {formData.hasShops && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Rent - Shops ({formData.areaShops || 0} sft × ₹{formData.rentPerSqftShop || 0}/sft/mo × {formData.shopRentDuration || formData.projectDuration} mo)</span>
                    <span className="font-mono font-medium">{formatCurrency(calculations.totalRentalCostShops || 0)}</span>
                  </div>
                )}
                <div className="text-xs text-muted-foreground mt-2 p-2 bg-background/50 rounded">
                  <p>• Flats/Offices: {formData.projectDuration || 0} months @ {formData.rentIncrementPA || 5}% PA increment</p>
                  {formData.hasShops && <p>• Shops: {formData.shopRentDuration || formData.projectDuration} months @ {formData.shopRentIncrement || formData.rentIncrementPA || 5}% PA increment</p>}
                </div>
                <Separator />
                <div className="flex justify-between font-semibold">
                  <span>Total Rental Cost (with compounding)</span>
                  <span className="font-mono">{formatCurrency(calculations.totalRentalCost || 0)}</span>
                </div>
              </div>
            </div>

            <Separator />

            {/* Other Costs */}
            <div className="space-y-2">
              <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">Other Costs</h4>
              <div className="space-y-2 text-sm bg-secondary/30 p-3 rounded-sm">
                {calculations.depositCost > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Refundable Deposit ({formData.depositMonths || 0} months rent)</span>
                    <span className="font-mono font-medium">{formatCurrency(calculations.depositCost)}</span>
                  </div>
                )}
                {calculations.brokerageCost > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Brokerage ({formData.brokerageMonths || 0} months rent)</span>
                    <span className="font-mono font-medium">{formatCurrency(calculations.brokerageCost)}</span>
                  </div>
                )}
                {calculations.pmcCost > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">PMC Charges</span>
                    <span className="font-mono font-medium">{formatCurrency(calculations.pmcCost)}</span>
                  </div>
                )}
                {calculations.bgCost > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Bank Guarantee Cost ({formData.bgRatePA}% × {formData.projectDuration} months × 50%)</span>
                    <span className="font-mono font-medium">{formatCurrency(calculations.bgCost)}</span>
                  </div>
                )}
              </div>
            </div>

            <Separator />

            {/* GST */}
            {calculations.totalGST > 0 && (
              <div className="space-y-2">
                <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">GST on Carpet Area</h4>
                <div className="space-y-2 text-sm bg-secondary/30 p-3 rounded-sm">
                  {formData.hasFlats && calculations.gstFlats > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        GST on Flats
                        <span className="text-xs ml-1">
                          (({calculations.areaFlatsSqMts?.toFixed(0)} + {calculations.flatFreeArea?.toFixed(0)} free) × 1.1 × ₹{Math.round(calculations.rrFlat)?.toLocaleString('en-IN')} × 5%)
                        </span>
                      </span>
                      <span className="font-mono font-medium">{formatCurrency(calculations.gstFlats)}</span>
                    </div>
                  )}
                  {formData.hasOffices && calculations.gstOffices > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        GST on Offices
                        <span className="text-xs ml-1">
                          (({calculations.areaOfficesSqMts?.toFixed(0)} + {calculations.officeFreeArea?.toFixed(0)} free) × 1.1 × ₹{Math.round(calculations.rrFlat)?.toLocaleString('en-IN')} × 5%)
                        </span>
                      </span>
                      <span className="font-mono font-medium">{formatCurrency(calculations.gstOffices)}</span>
                    </div>
                  )}
                  {formData.hasShops && calculations.gstShops > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        GST on Shops
                        <span className="text-xs ml-1">
                          (({calculations.areaShopsSqMts?.toFixed(0)} + {calculations.shopFreeArea?.toFixed(0)} free) × 1.1 × ₹{Math.round(calculations.rrShop)?.toLocaleString('en-IN')} × 5%)
                        </span>
                      </span>
                      <span className="font-mono font-medium">{formatCurrency(calculations.gstShops)}</span>
                    </div>
                  )}
                  <Separator />
                  <div className="flex justify-between font-semibold">
                    <span>Total GST</span>
                    <span className="font-mono">{formatCurrency(calculations.totalGST)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Parking Compensation Payout */}
            {calculations.parkingCompensationPayout > 0 && (
              <div className="bg-amber-50/50 p-4 rounded-sm border border-amber-200">
                <div className="flex justify-between items-center">
                  <div>
                    <span className="font-semibold">Parking Compensation Payout</span>
                    <p className="text-xs text-muted-foreground">Payout for {formData.existingParkings} existing parkings</p>
                  </div>
                  <span className="font-mono font-bold text-amber-700">{formatCurrency(calculations.parkingCompensationPayout)}</span>
                </div>
              </div>
            )}

            <div className="bg-chart-1/10 p-4 rounded-sm border border-chart-1/30">
              <div className="flex justify-between items-center">
                <span className="font-semibold text-lg">Total Society Expenses</span>
                <span className="font-mono font-bold text-2xl text-chart-1">{formatCurrency(calculations.totalSocietyExpenses)}</span>
              </div>
            </div>

            <div className="flex justify-end">
              <Button variant="outline" size="sm" className="rounded-sm" onClick={exportSocietyPDF} data-testid="export-society-pdf">
                <Download className="mr-2 h-4 w-4" />
                Export PDF
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SocietyFinancials;
